import XHR from '../utils/xhr';
import { cookieExists } from '../utils/cookies';
import { dispatchAction } from '../dispatcher/AppDispatcher';
import BookingFormActionTypes from '../constants/actionTypes/BookingForm';
import ConfigStore from '../stores/Config';
import ServiceListStore from '../stores/ServiceList';
import AvailableTimeSelectionStore from '../stores/AvailableTimeSelection';
import BookingFormStore from '../stores/BookingForm';
import { formErrors } from '../constants/errors';
import { costTypes } from '../constants/costTypes';
import renderModes from '../constants/renderModes';
import RouterHelper from '../utils/RouterHelper';
import { trackPixel } from '../utils/FacebookPixel';

const isPublishMode = () => {
  const { renderMode } = ConfigStore.getState();

  return renderMode === renderModes.PUBLISH;
};

const mapQuestionToAnswer = answers => question => {
  const answer = answers.find(answr => answr.questionId === question.id);
  return { question_id: question.id, body: answer ? answer.body : '' };
};

const getCurrentBookingData = () => {
  const { selectedAvailableTime, selectedResource } = AvailableTimeSelectionStore.getState();
  const { selectedService } = ServiceListStore.getState();

  const { client, location, membership, answers, marketing } = BookingFormStore.getState();
  const { questions } = ConfigStore.getState();

  return {
    client,
    location,
    marketing_consent: marketing,
    answers: questions.map(mapQuestionToAnswer(answers)),
    signup: membership,
    service_id: selectedService.id,
    availability_time: selectedAvailableTime,
    resource_ids: [selectedResource.id],
    // For now, quantity is hardcoded to 1.
    // TODO: Make quantity dynamic once group services are introduced.
    quantity: 1,
    ssl: window.location.protocol === 'https:' ? 'true' : 'false'
  };
};

// NOTE: This method does NOT catch errors
const saveBooking = (extraAttrs = {}) => {
  if (!isPublishMode()) return;

  const bookingData = {
    ...getCurrentBookingData(),
    ...extraAttrs
  };

  const { olaApiAccountsPath } = ConfigStore.getState();
  const { isC2LoggedIn } = BookingFormStore.getState();
  const { contactId } = bookingData.client;

  dispatchAction(requestSaveBooking());

  const url = isC2LoggedIn
    ? `/m/api/ola${olaApiAccountsPath}/c2/contacts/${contactId}/bookings`
    : `${ConfigStore.getApiBaseUrl()}/bookings`;

  return XHR.postRequest(url, { data: bookingData }).then(res => {
    const { booking } = res;
    dispatchAction(receiveBooking(booking));

    return true;
  });
};

export function clearErrors() {
  dispatchAction({
    type: BookingFormActionTypes.CLEAR_ERRORS
  });
}

export function updateSingleAnswer(questionId, body) {
  const { answers } = BookingFormStore.getState();

  const updatedAnswers = [
    ...answers.filter(answer => answer.questionId !== questionId),
    { questionId, body }
  ];

  updateAnswers(updatedAnswers);
}

function updateAnswers(answers) {
  dispatchAction({
    type: BookingFormActionTypes.UPDATE_ANSWERS,
    data: answers
  });
}

export function updateClient(client) {
  dispatchAction({
    type: BookingFormActionTypes.UPDATE_CLIENT,
    data: client
  });
}

export function updateLocation(location) {
  dispatchAction({
    type: BookingFormActionTypes.UPDATE_LOCATION,
    data: location
  });
}

export function updateMembership(membership) {
  dispatchAction({
    type: BookingFormActionTypes.UPDATE_MEMBERSHIP,
    data: membership
  });
}

export function updateMarketing(marketing) {
  dispatchAction({
    type: BookingFormActionTypes.UPDATE_MARKETING,
    data: marketing
  });
}

function requestSaveBooking() {
  return {
    type: BookingFormActionTypes.REQUEST_SAVE_BOOKING
  };
}

function receiveBooking(booking = {}) {
  return {
    type: BookingFormActionTypes.RECEIVE_BOOKING,
    data: booking
  };
}

function errorReceiveBooking(error) {
  return {
    type: BookingFormActionTypes.ERROR_RECEIVE_BOOKING,
    data: error
  };
}

export function receivePaypalError(error) {
  return {
    type: BookingFormActionTypes.RECEIVE_PAYPAL_ERROR,
    data: error
  };
}

export function startPayment() {
  if (!isPublishMode()) return;

  dispatchAction({
    type: BookingFormActionTypes.START_PAYMENT
  });
}

export function cancelPayment() {
  if (!isPublishMode()) return;

  dispatchAction({
    type: BookingFormActionTypes.CANCEL_PAYMENT
  });
}

function requestActivePaymentMethods() {
  return {
    type: BookingFormActionTypes.REQUEST_ACTIVE_PAYMENT_METHODS
  };
}

function receiveActivePaymentMethods(activePaymentMethods) {
  return {
    type: BookingFormActionTypes.RECEIVE_ACTIVE_PAYMENT_METHODS,
    data: activePaymentMethods
  };
}

function errReceiveActivePaymentMethods(error) {
  return {
    type: BookingFormActionTypes.ERROR_RECEIVE_ACTIVE_PAYMENT_METHODS,
    data: error
  };
}

export function fetchActivePaymentMethods(total) {
  dispatchAction(requestActivePaymentMethods());
  const ssl = window.location.protocol === 'https:' ? 'true' : 'false';
  total = total || '0';
  const {
    locale,
    securepayApiHost,
    currency_format: { iso_code: currency }
  } = ConfigStore.getState();
  const url = `${securepayApiHost}/payment_methods/active`;

  return XHR.getRequest(url, {
    params: {
      ssl,
      total,
      currency,
      locale
    }
  })
    .then(res => {
      dispatchAction(receiveActivePaymentMethods(res.active_payment_methods));
    })
    .catch(res => {
      dispatchAction(errReceiveActivePaymentMethods(res.response || res));
    });
}

export function saveSquareBooking({ paymentToken, verificationToken, paymentMethod }) {
  return saveBooking({
    securepay_payment_details: {
      payment_token: paymentToken,
      verification_token: verificationToken,
      payment_method: paymentMethod
    }
  }).catch(res => {
    dispatchAction(errorReceiveBooking(res.response || res));

    return false;
  });
}

export function savePaypalBooking(data) {
  const {
    featureFlags: { debugOlaPaypalAuthorize }
  } = ConfigStore.getState();

  if (debugOlaPaypalAuthorize) {
    console.warn('Ola paypal data:', data); // eslint-disable-line no-console
  }

  return saveBooking({
    securepay_payment_details: {
      payment_method: 'paypal',
      payment_id: data.paymentID,
      payer_id: data.payerID
    }
  }).catch(res => {
    if (res && res.response) {
      if (formErrors.includes(res.response.type)) {
        dispatchAction(errorReceiveBooking(res.response));
      } else {
        dispatchAction(receivePaypalError(res.response));
      }
    } else {
      dispatchAction(receivePaypalError(res));
    }

    return false;
  });
}

export function saveUnpaidBooking() {
  return saveBooking().catch(res => {
    dispatchAction(errorReceiveBooking(res.response || res));

    return false;
  });
}

export function initPaypal() {
  const { isFetching } = BookingFormStore.getState();
  if (isFetching || !isPublishMode()) return null;

  startPayment();

  /* eslint-disable camelcase */
  const { service_id, availability_time, resource_ids, quantity } = getCurrentBookingData();

  const data = {
    service_id,
    resource_ids,
    quantity,
    available_time: availability_time,
    securepay_payment_details: { payment_method: 'paypal' }
  };
  /* eslint-enable camelcase */

  return XHR.postRequest(`${ConfigStore.getApiBaseUrl()}/payments/init`, { data })
    .then(res => {
      return res.securepay_payment_details.payment_id;
    })
    .catch(res => {
      dispatchAction(receivePaypalError(res.response || res));
      return null;
    });
}

const requestRavenContact = () => {
  dispatchAction({
    type: BookingFormActionTypes.REQUEST_RAVEN_CONTACT
  });
};

export const receiveRavenContact = ({ contactId, email, nameFirst, nameLast, phones }) => {
  const phoneNumber = (Array.isArray(phones) && phones[0] && phones[0].phoneNumber) || '';

  dispatchAction({
    type: BookingFormActionTypes.RECEIVE_RAVEN_CONTACT
  });

  updateClient({
    first_name: nameFirst,
    last_name: nameLast,
    email,
    contactId,
    phone_number: phoneNumber
  });
};

export const setNoRavenContact = () => {
  const { isC2LoggedIn: wasC2LoggedIn } = BookingFormStore.getState();

  dispatchAction({
    type: BookingFormActionTypes.SET_NO_RAVEN_CONTACT
  });

  // only clear client data if user has recently logged out
  if (wasC2LoggedIn) {
    updateClient({
      first_name: null,
      last_name: null,
      email: null,
      contactId: null,
      phone_number: ''
    });
  }
};

export const getRavenContact = () => {
  if (!(isPublishMode() && cookieExists('info_c2'))) {
    return Promise.resolve(setNoRavenContact());
  }

  requestRavenContact();

  return XHR.getRequest('/m/api/crm/v2/crm/contacts/self', {
    headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
  })
    .then(receiveRavenContact)
    .catch(setNoRavenContact);
};

export const addCurrentBookingToCart = ({ cart, currentPageRoute }) => {
  const { availability_time: availableTime, ...bookingData } = getCurrentBookingData();
  const { selectedService: service } = ServiceListStore.getState();
  const deepLink = RouterHelper.buildServicePath(service.slug, currentPageRoute);

  // service.cost will always be C1 entered value.
  service.cost =
    service.tax_category && service.tax_category.tax_included_in_price
      ? service.cost_with_tax
      : service.cost;
  delete service.cost_with_tax;

  const serviceData = {
    content_name: service.name,
    content_type: 'service',
    currency: ConfigStore.getState().currency_format.iso_code,
    value: Number(service.cost)
  };

  trackPixel('AddToCart', serviceData);

  const data = {
    ...bookingData,
    service,
    available_time: availableTime,
    deep_link: deepLink,
    is_custom_cost: service.cost_type === costTypes.CUSTOM_TEXT
  };

  cart.addItem({
    type: 'service',
    data
  });
};
